<template lang="pug">
.car-plate-field-wrapper
  .form-field-label
    span {{ label }}
    span.form-field-require-tag(v-show="required", :title="notifies.required_field")
      | *

  .car-plate-field
    .form-field.form-multiselect.car-plate-field__type
      multiselect(
        v-model="selectedPlateType",
        track-by="value",
        label="title",
        :options="availablePlateTypes",
        :allow-empty="false",
        :show-labels="false",
        :searchable="false",
        @select="handlePlateTypeSelect"
      )
        template(slot="singleLabel", slot-scope="props")
          .car-plate-option.car-plate-option--selected
            span.car-plate-option__preview(
              :class="{ 'car-plate-option__preview--foreign': props.option.value === 'foreign' }",
              :style="previewStyle(props.option)"
            )
              span.car-plate-option__country-band(v-if="props.option.value === 'foreign'")
              span.car-plate-option__number {{ previewValue(props.option) }}

        template(slot="option", slot-scope="props")
          .car-plate-option
            span {{ props.option.title }}
            span.car-plate-option__preview(
              :class="{ 'car-plate-option__preview--foreign': props.option.value === 'foreign' }",
              :style="previewStyle(props.option)"
            )
              span.car-plate-option__country-band(v-if="props.option.value === 'foreign'")
              span.car-plate-option__number {{ previewValue(props.option) }}

    div(:class="[currentFieldIsInvalid ? `${mainClass} valid-error` : mainClass]")
      q-input(
        ref="plateInput",
        filled,
        no-error-icon,
        autocomplete="off",
        :value="displayPlateValue",
        :for="name",
        @input="handleInput",
        @paste.native="handlePaste",
        @keydown.native="handlePlateKeydown",
        @beforeinput.native="handleBeforeInput"
      )
        q-icon.cancel-string-field(name="cancel", v-if="plateValue", @click.stop="resetField")

  .valid-error-message(v-if="currentFieldIsInvalid") {{ currentErrorMessage }}
</template>

<script setup>
import { computed, nextTick, onBeforeMount, ref } from "vue";
import { useStore } from "@/store";
import { currentLocale, notifies } from "@/services/useLocales";
import i18n from "@/plugins/vue-i18n/index";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";

const {
  CAR_PLATE_TYPES,
  displayCarPlateInCyrillic,
  isCompleteCarPlate,
  normalizeCarPlateValue,
  transliterateCarPlate,
} = require("@/services/useCarPlate");

const props = defineProps({
  parentData: {
    type: Object,
    default: () => ({}),
  },
});

const store = useStore();
const field = props.parentData.data[0];
const grid = props.parentData.grid;
const name = field.name;
const label = field.label;
const required = Boolean(field.require);
const initialValue = field.value;
const plateType = field.plate_type;
const plateTypes = field.plate_types || [];
const mainClass = "form-field form-input car-plate-field__input";

const plateInput = ref(null);
const selectedPlateType = ref(null);
const validErrorMessage = ref(null);

const carPlateLocales = computed(() => i18n.messages[currentLocale.value].carPlateTypes);
const carPlateFieldLocales = computed(() => i18n.messages[currentLocale.value].carPlateField);
const availablePlateTypes = computed(() =>
  plateTypes
    .map(type => {
      const config = CAR_PLATE_TYPES[type];
      return config ? { ...config, title: carPlateLocales.value[type] } : null;
    })
    .filter(Boolean),
);
const currentPlateType = computed(() => selectedPlateType.value?.value);
const currentForm = computed(() => store.state.grid[grid].form);
const currentField = computed(() => currentForm.value[name] || {});
const currentFieldIsInvalid = computed(() => currentForm.value.invalid_fields?.[name]);
const customErrorMessage = computed(() => currentForm.value.invalid_fields_errors?.[name] || "");
const currentErrorMessage = computed(() => validErrorMessage.value || customErrorMessage.value);

const previewStyle = option => ({
  backgroundColor: option.background,
  color: option.color,
});

const previewValue = option => displayCarPlateInCyrillic(option.example, option.value);

const invalid = (value, currentType) => {
  if (!value) {
    validErrorMessage.value = required ? notifies.value.not_empty : null;
    return Boolean(required);
  }

  if (!isCompleteCarPlate(value, currentType)) {
    validErrorMessage.value = carPlateFieldLocales.value.invalidFormat;
    return true;
  }

  validErrorMessage.value = null;
  return false;
};

const updateField = (value, currentType) => {
  const normalizedValue = normalizeCarPlateValue(value, currentType);
  const result = {
    field: {
      value: normalizedValue,
      plate_type: currentType,
    },
    invalid: invalid(normalizedValue, currentType),
  };

  store.commit("updateFormField", { grid_name: grid, field: name, value: result });

  if (!result.invalid) {
    store.commit("resetFormFieldValue", {
      grid_name: grid,
      field: "invalid_fields",
      value: name,
    });
  }
};

const plateValue = computed({
  get: () => currentField.value.field?.value || "",
  set: value => updateField(value, currentPlateType.value),
});

const displayPlateValue = computed({
  get: () => displayCarPlateInCyrillic(plateValue.value, currentPlateType.value),
  set: value => {
    plateValue.value = value;
  },
});

const setCaretToEnd = () => {
  nextTick(() => {
    const input = plateInput.value?.getNativeElement();
    if (!input) return;

    const end = input.value.length;
    plateInput.value.focus();
    input.setSelectionRange(end, end, "forward");
  });
};

const clearFieldValidationDisplay = () => {
  store.commit("resetFormFieldValue", {
    grid_name: grid,
    field: "invalid_fields",
    value: name,
  });
};

const handleInput = value => {
  displayPlateValue.value = value;
  clearFieldValidationDisplay();
};

const handlePlateTypeSelect = option => {
  selectedPlateType.value = option;
  updateField("", option.value);
  clearFieldValidationDisplay();
  nextTick(() => plateInput.value?.focus());
};

const handlePaste = event => {
  if (currentPlateType.value === "foreign") return;

  const pastedText = event.clipboardData?.getData("text") || "";
  event.preventDefault();
  plateValue.value = pastedText;
  clearFieldValidationDisplay();
  setCaretToEnd();
};

const handlePlateKeydown = event => {
  if (
    currentPlateType.value === "foreign" ||
    event.ctrlKey ||
    event.metaKey ||
    event.altKey ||
    event.key.length !== 1
  ) {
    return;
  }

  const upperCaseKey = event.key.toUpperCase();
  const normalizedCharacter = transliterateCarPlate(upperCaseKey);
  const isAllowedPlateCharacter = /\d/.test(normalizedCharacter) || /^[ABCEHKMOPTXY]$/.test(normalizedCharacter);

  if (!isAllowedPlateCharacter) {
    event.preventDefault();
  }
};

const handleBeforeInput = event => {
  if (currentPlateType.value === "foreign" || !event.data) return;

  const normalizedCharacter = transliterateCarPlate(event.data);
  if (!/^[0-9ABCEHKMOPTXY]$/.test(normalizedCharacter)) {
    event.preventDefault();
  }
};

const resetField = () => {
  displayPlateValue.value = "";
  clearFieldValidationDisplay();
};

onBeforeMount(() => {
  selectedPlateType.value =
    availablePlateTypes.value.find(type => type.value === plateType) || availablePlateTypes.value[0] || null;
  store.commit("createFormField", { grid_name: grid, field: name });
  updateField(initialValue || "", currentPlateType.value);
});
</script>

<style lang="scss">
@import "../../../../assets/styles/forms/fields/string";

.car-plate-field {
  display: grid;
  grid-template-columns: 150px minmax(0, 1fr);
  gap: 10px;
  min-width: 0;
  width: 100%;

  &__type,
  &__input {
    margin: 0;
  }

  &__type {
    min-width: 0;

    &.form-multiselect .multiselect .multiselect__tags {
      display: flex;
      align-items: center;
      height: var(--field-min-height);
      padding-top: 0;

      .multiselect__single {
        display: flex;
        align-items: center;
        min-height: 0;
        margin-top: 0;
        margin-bottom: 0;
      }
    }

    .multiselect__select {
      width: 30px;
    }

    .multiselect__tags {
      padding-right: 30px;
    }

    .multiselect__content-wrapper {
      right: auto;
      width: 380px;
      max-width: calc(100vw - 48px);
    }
  }

  &__input {
    min-width: 0;
    width: 100%;

    .q-field,
    .q-field__inner,
    .q-field__control {
      height: var(--field-min-height) !important;
    }

    .q-field__inner {
      top: 0;
    }

    .q-field__control,
    .q-field__control-container,
    .q-field__append {
      align-items: center;
    }

    .q-field__control-container {
      min-width: 0;
      width: 100%;
    }

    .q-field__native {
      align-self: center;
      box-sizing: border-box;
      margin-top: 0 !important;
      padding-top: 0;
      padding-right: 15px !important;
      padding-bottom: 0;
      width: 100% !important;
    }

    .q-field__append {
      position: absolute;
      top: 0;
      right: 0;
      width: 30px;
      height: 100%;
      padding: 0;
    }

    .q-field .cancel-string-field {
      position: absolute;
      top: 50%;
      right: 4px;
      margin: 0;
      transform: translateY(-50%);
    }
  }
}

.car-plate-field-wrapper {
  min-width: 0;
  width: 100%;
}

.car-plate-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;

  &--selected {
    justify-content: center;
  }

  &__preview {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 106px;
    height: 26px;
    padding: 3px 6px;
    overflow: hidden;
    border: 1px solid #8d939f;
    border-radius: 3px;
    box-sizing: border-box;
    font-size: 11px;
    font-weight: 700;
    line-height: 1.2;
    text-align: center;

    &--foreign {
      justify-content: flex-start;
      padding: 0;
    }
  }

  &__country-band {
    align-self: stretch;
    flex: 0 0 18px;
    background: #2459a9;
    border-right: 1px solid #16427f;
  }

  &__number {
    flex: 1;
  }
}

@media (max-width: 720px) {
  .car-plate-field {
    &__type {
      width: 150px;
    }
  }
}
</style>
